import { supabasePlugin } from "@/plugins/supabase.ts";
import exception from "@/utils/exception";
import { useUserStore } from "@/stores/userStore";
import Api from "../api";
import Helper from "../helper";

async function signInWithAzure(to) {
  try {
    // here read the path from the to object and look for query email and pull that value and set as login_hint
    const email = to.query.email;
    const redirectUrl = Helper.isNativePlatForm()
      ? import.meta.env.VITE_SUPABSE_AUTH_CALLBACK_APP_URL
      : import.meta.env.VITE_SUPABSE_AUTH_CALLBACK_URL;
    
    const { data, error } = await supabasePlugin.client.auth.signInWithOAuth({
      provider: import.meta.env.VITE_SUPABSE_PROVIDER,
      options: {
        scopes: import.meta.env.VITE_SUPABSE_PROVIDER_SCOPES,
        redirectTo: redirectUrl,
        queryParams: {
          login_hint: email ? email : ""
        }
      }
    });
    return { data, error };
  } catch (error) {
    exception.catchException(error);
    return error;
  }
}

async function signInWithEmail(email, password) {
  try {
    const { data, error } = await supabasePlugin.client.auth.signInWithPassword(
      {
        email: email,
        password: password
      }
    );

    if (error) {
      return { data: null, error };
    }

    if (data.session) {
      // Set user session and data like in OAuth flow
      await setUserSession(data.session);
    }

    return { data, error: null };
  } catch (error) {
    exception.catchException(error);
    return { data: null, error };
  }
}

async function signOut() {
  try {
    return await supabasePlugin.client.auth.signOut();
  } catch (error) {
    exception.catchException(error);
    return error;
  }
}

async function setUserSession(data) {
  try {
    console.log("SET-SESSION :", data);
    const userStore = useUserStore();
    // updating session for supabase
    await supabasePlugin.client.auth.setSession(data);
    userStore.setUserSession(data);
    console.log("setUserSession - session set, fetching user data...");
    let _temp = await getCurrentUserData();
    userStore.setUserData(_temp);
    userStore.setIsUserSignedIn(true);
    console.log("setUserSession - user data set in store");
  } catch (error) {
    console.error("setUserSession - error:", error);
    exception.catchException(error);
    return error;
  }
}

async function refreshSession() {
  try {
    console.log("REFRESH-SESSION ----------------");
    const userStore = useUserStore();
    const refresh_token = userStore.getRefreshToken();
    let _res = {
      data: null,
      error: null
    };
    if (refresh_token) {
      _res = await supabasePlugin.client.auth.refreshSession({
        refresh_token
      });
    } else {
      _res = await supabasePlugin.client.auth.getSession();
    }
    console.log("REFRESH SESSION RESPONSE :", _res);
    if (_res.error || !_res.data?.session) {
      // failed
      return false;
    }
    await setUserSession(_res.data.session);
    // success
    return true;
  } catch (err) {
    console.error(
      "--------------------------------Unexpected refresh error:",
      err
    );
  }
}

async function isUserSignedIn() {
  try {
    const userStore = useUserStore();
    if (userStore.isOnline && Helper.isSessionExpiringSoon().isExpired) {
      await userStore.setIsUserSignedIn(await refreshSession());
    }
    return userStore.isUserSignedIn();
  } catch (error) {
    exception.catchException(error);
    console.log("ERROR : ", error);
    return error;
  }
}

async function getCurrentUserData() {
  try {
    let userData = await supabasePlugin.client.auth.getUser();
    let _user = userData["data"]["user"];
    console.log("getCurrentUserData - auth user:", _user);

    if (_user) {
      // find user id
      let userId = _user.user_metadata?.custom_claims?.oid;
      // Try to get profile data with retries
      let profileData = null;
      let profileError = null;

      for (let attempt = 1; attempt <= 3; attempt++) {
        const { data, error } = await Api.Profiles.getProfile(userId);
        // console.log("getCurrentUserData - profile API response:", {
        //   data,
        //   error
        // });

        if (error) {
          console.error(
            `getCurrentUserData - profile API error (attempt ${attempt}):`,
            error
          );
          profileError = error;
          // Wait before retry
          if (attempt < 3) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        } else if (data) {
          profileData = data;
          break;
        } else {
          console.warn(
            `getCurrentUserData - no profile data returned (attempt ${attempt})`
          );
          if (attempt < 3) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }
      }

      if (profileError) {
        throw profileError;
      }

      if (profileData) {
        userData["data"]["user"] = {
          ...userData["data"]["user"],
          ...profileData
        };
      } else {
        console.warn(
          "getCurrentUserData - no profile data returned after all attempts"
        );
      }
    } else {
      console.warn("getCurrentUserData - no auth user found");
    }

    return userData["data"]["user"];
  } catch (error) {
    console.error("getCurrentUserData - exception:", error);
    exception.catchException(error);
    return error;
  }
}

export default {
  signInWithAzure,
  signInWithEmail,
  signOut,
  getCurrentUserData,
  refreshSession,
  setUserSession,
  isUserSignedIn
};
